/**
 * Canonical entry_source → visual config + budget-chip metadata. One mapping
 * used everywhere.
 *
 * Colour key (consistent across positions, schedule, orders):
 *   WAT              → purple  (wait-and-trade)
 *   ORB              → amber   (opening-range breakout)
 *   RE_ENTRY         → blue    (simple re-entry after exit)
 *   RE_COST          → indigo  (re-cost — fresh strike after SL hit)
 *   SL_REEXEC        → cyan    (SL re-execution — LTP-based new position;
 *                              distinct from RE_ENTRY blue per canonical chip palette)
 *   TP_REEXEC        → emerald (take-profit re-execution)
 *   MANUAL_RETRY     → orange  (human-initiated retry)
 *   MSLC             → sky     (move SL to cost — strategy-level trigger)
 *   TSL              → violet  (trailing stop-loss — ratcheted)
 *   SCHEDULED / ENTRY → not rendered (default path, no badge)
 *
 * Usage:
 *   <EntrySourceBadge
 *     source={p.entrySource}
 *     reEntryCount={p.reEntryCount}
 *     tpReexecCount={p.tpReexecCount}
 *     reEntryConfig={p.reEntryConfig}
 *     reExecuteConfig={p.reExecuteConfig}
 *   />
 *
 * The badge renders a config-driven N/M chip cluster:
 *   1. Label-only chip for non-numbered entry sources (WAT / ORB / MANUAL_RETRY
 *      / MSLC / TSL). Numbered sources (SL_REENTRY / SL_RECOST / SL_REEXEC /
 *      TP_REEXEC / RE_ENTRY / RE_COST) don't get a standalone chip — the budget
 *      chip below carries that info via its N counter.
 *   2. "Re-Entry|Re-Cost|Re-Exec N/M" chip when reEntryConfig.enabled, with
 *      label + colour chosen by reEntryConfig.type.
 *   3. "TP-Exec N/M" chip when reExecuteConfig.enabled.
 *
 * `buildEntryChips` exposes the same chip-list computation as a pure helper —
 * use it from non-badge surfaces (e.g. tests, CSV exports) that need the same
 * semantics without React.
 */

import { CHIP_DENSE } from './chipColors';
import { pluralise } from './pluralise';

export type EntrySourceVariant =
  | 'purple' | 'amber' | 'blue' | 'indigo'
  | 'emerald' | 'orange' | 'cyan' | 'sky' | 'violet';

export interface EntrySourceConfig {
  variant: EntrySourceVariant;
  label: string;      // compact badge label
  tooltipBase: string; // tooltip text (sequence suffix appended when applicable)
  /** Whether to append `· {sequenceNo}` when sequenceNo > 0. */
  numbered: boolean;
}

/** Source values that should NOT render any badge (ordinary scheduled entry). */
const SILENT_SOURCES = new Set(['', 'SCHEDULED', 'ENTRY']);

const ENTRY_SOURCE_CONFIG: Record<string, EntrySourceConfig> = {
  // Wait-and-trade — both server aliases
  WAT: {
    variant: 'purple',
    label: 'WAT',
    tooltipBase: 'Wait-and-trade — order fired on candle-close trigger',
    numbered: false,
  },
  WAIT_AND_TRADE: {
    variant: 'purple',
    label: 'WAT',
    tooltipBase: 'Wait-and-trade — order fired on candle-close trigger',
    numbered: false,
  },

  // Opening Range Breakout
  ORB: {
    variant: 'amber',
    label: 'ORB',
    tooltipBase: 'Opening Range Breakout',
    numbered: false,
  },

  // Simple re-entry (re-enters the SAME strike after prior exit)
  RE_ENTRY: {
    variant: 'blue',
    label: 'RE-ENTRY',
    tooltipBase: 'Re-entry attempt after prior exit',
    numbered: true,
  },
  SL_REENTRY: {
    variant: 'blue',
    label: 'RE-ENTRY',
    tooltipBase: 'Re-entry attempt after prior exit',
    numbered: true,
  },

  // Re-cost — fresh strike selection after SL hit
  RE_COST: {
    variant: 'indigo',
    label: 'RE-COST',
    tooltipBase: 'Re-cost — fresh strike after SL hit',
    numbered: true,
  },
  SL_RECOST: {
    variant: 'indigo',
    label: 'RE-COST',
    tooltipBase: 'Re-cost — fresh strike after SL hit',
    numbered: true,
  },

  // SL re-execution — NEW position opened with LTP on SL fill.
  // Distinct cyan hue per canonical chip palette so it visually separates
  // from RE_ENTRY (blue) which it would otherwise share.
  SL_REEXEC: {
    variant: 'cyan',
    label: 'RE-EXEC',
    tooltipBase: 'SL re-execution — new position with LTP on SL fill',
    numbered: true,
  },

  // Take-profit re-execution
  TP_REEXEC: {
    variant: 'emerald',
    label: 'TP-EXEC',
    tooltipBase: 'Take-profit re-execution',
    numbered: true,
  },

  // Manual retry by the trader
  MANUAL_RETRY: {
    variant: 'orange',
    label: 'RETRY',
    tooltipBase: 'Manually retried entry',
    numbered: false,
  },

  // Move-SL-to-Cost — strategy-level event that shifts SL trigger to entry cost.
  // Not a real entry source from the backend (no MSLC entry_source value), but
  // pre-registered here so future surfaces can render an MSLC chip via the
  // shared badge component if needed.
  MSLC: {
    variant: 'sky',
    label: 'MSLC',
    tooltipBase: 'Move stop-loss to cost — strategy-level trigger',
    numbered: false,
  },

  // Trailing Stop-Loss ratcheted — visual marker for surfaces that want to
  // flag positions whose SL has been ratcheted by TSL.
  TSL: {
    variant: 'violet',
    label: 'TSL',
    tooltipBase: 'Trailing stop-loss — SL trigger ratcheted',
    numbered: false,
  },
};

/** Returns the badge config for a source, or null when no badge should render. */
export function getEntrySourceConfig(source: string | null | undefined): EntrySourceConfig | null {
  if (!source || SILENT_SOURCES.has(source)) return null;
  return ENTRY_SOURCE_CONFIG[source] ?? null;
}

/**
 * Variant → Tailwind classes for the "filled" (pill) badge variant.
 * Sourced from canonical `CHIP_DENSE` palette so a single change to a hue
 * propagates everywhere.
 */
export const ENTRY_SOURCE_FILLED_CLASSES: Record<EntrySourceVariant, string> = {
  purple:  CHIP_DENSE.WAT,
  amber:   CHIP_DENSE.ORB,
  blue:    CHIP_DENSE.RE_ENTRY,
  indigo:  CHIP_DENSE.RE_COST,
  emerald: CHIP_DENSE.RE_EXEC_TP,
  orange:  CHIP_DENSE.MANUAL_RETRY,
  cyan:    CHIP_DENSE.RE_EXEC_SL,
  sky:     CHIP_DENSE.MSLC,
  violet:  CHIP_DENSE.TSL,
};

/**
 * Variant → Tailwind classes for the "text" (inline colored text) variant.
 * Text-only (no bg) — kept inline since these aren't really chips, just
 * colored inline labels.
 */
export const ENTRY_SOURCE_TEXT_CLASSES: Record<EntrySourceVariant, string> = {
  purple:  'text-purple-600 dark:text-purple-400',
  amber:   'text-amber-600 dark:text-amber-400',
  blue:    'text-blue-600 dark:text-blue-400',
  indigo:  'text-indigo-600 dark:text-indigo-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  orange:  'text-orange-600 dark:text-orange-400',
  cyan:    'text-cyan-600 dark:text-cyan-400',
  sky:     'text-sky-600 dark:text-sky-400',
  violet:  'text-violet-600 dark:text-violet-400',
};

/**
 * Single source of truth for entry-source → colour variant. Used by both the
 * config-driven `EntrySourceBadge` cluster (via `getReEntryFlavourMeta`) AND
 * the chain-depth `ReSequenceBadge` chip (via `reSequenceChipMeta` in
 * `positionDisplay.tsx`). Keeping the colour rule in one place stops the two
 * surfaces from drifting (e.g. badge cluster goes cyan for SL_REEXEC, round
 * chip stays blue).
 *
 * Falls back to `blue` for SL_REENTRY / legacy `RE_ENTRY` alias / null /
 * unknown — matches the historical default colour.
 */
export function getEntrySourceVariant(
  source: string | null | undefined,
): EntrySourceVariant {
  if (source === 'SL_RECOST' || source === 'RE_COST') return 'indigo';
  if (source === 'SL_REEXEC') return 'cyan';
  if (source === 'TP_REEXEC') return 'emerald';
  return 'blue';
}

/**
 * Lineage source of a chain child. A UL wait-and-trade re-wait child is
 * stamped entry_source=WAIT_AND_TRADE (the UL firer's routing key), so its
 * lineage is the re-entry type it carries.
 */
export function chainChildSource(
  entrySource: string | null | undefined,
  reEntryType: string | null | undefined,
): string | null | undefined {
  return entrySource === 'WAIT_AND_TRADE' && reEntryType ? reEntryType : entrySource;
}

/**
 * Per-leg budget-pill metadata — label + colour variant — for the SL re-entry
 * flavour configured on a leg. Used by `EntrySourceBadge` to render the
 * "Re-Entry 2/5" / "Re-Cost 2/5" / "Re-Exec 2/5" budget chip.
 *
 * Domain: SL-side re-entry only. Legal inputs are `SL_REENTRY` / `SL_RECOST` /
 * `SL_REEXEC` (plus the legacy `RE_ENTRY` / `RE_COST` aliases). For any other
 * value — including the cross-domain `TP_REEXEC` — fall back to the default
 * Re-Entry / blue pairing. (Without this guard the variant lookup would
 * return emerald for TP_REEXEC, producing a label-says-Re-Entry-chip-is-
 * emerald mismatch. The fallback keeps label and colour consistent for any
 * illegal input.)
 */
export function getReEntryFlavourMeta(
  type: string | null | undefined,
): { label: string; variant: EntrySourceVariant } {
  if (type === 'SL_RECOST' || type === 'RE_COST') {
    return { label: 'Re-Cost', variant: getEntrySourceVariant(type) };
  }
  if (type === 'SL_REEXEC') {
    return { label: 'Re-Exec', variant: getEntrySourceVariant(type) };
  }
  // Default fallback: SL_REENTRY / RE_ENTRY / null / unknown / non-SL inputs.
  // Always blue + Re-Entry — label and colour must agree, so we hard-code
  // 'blue' here instead of routing through getEntrySourceVariant (which would
  // return 'emerald' for the illegal TP_REEXEC input).
  return { label: 'Re-Entry', variant: 'blue' };
}

/** TP re-execute budget-pill metadata. */
export const TP_REEXEC_PILL_META = {
  label: 'TP-Exec',
  variant: 'emerald' as EntrySourceVariant,
} as const;

/** One chip in the entry-source cluster — pure data, no React. */
export interface EntryChipDescriptor {
  label: string;
  variant: EntrySourceVariant;
  tooltip: string;
}

/**
 * Build the chip list for an EntrySourceBadge given the row's data.
 *
 * Pure function — no React, no DOM — so it can be tested directly and reused
 * from non-badge surfaces. Mirrors EntrySourceBadge's render rules exactly:
 *
 *   1. Append a label-only chip for non-numbered entry sources (WAT / ORB /
 *      MANUAL_RETRY / MSLC / TSL). Numbered sources skip this — the budget
 *      chip's N counter encodes that info.
 *   2. Append a "Re-Entry|Re-Cost|Re-Exec N/M" chip when reEntryConfig.enabled.
 *   3. Append a "TP-Exec N/M" chip when reExecuteConfig.enabled.
 *
 * Returns `[]` when nothing applies (badge renders no DOM in that case).
 */
export function buildEntryChips(
  source: string | null | undefined,
  reEntryCount: number | null | undefined,
  tpReexecCount: number | null | undefined,
  reEntryConfig: { enabled: boolean; type: string; count: number } | null | undefined,
  reExecuteConfig: { enabled: boolean; type: string; count: number } | null | undefined,
): EntryChipDescriptor[] {
  const chips: EntryChipDescriptor[] = [];

  const sourceCfg = getEntrySourceConfig(source);
  if (sourceCfg && !sourceCfg.numbered) {
    chips.push({
      label: sourceCfg.label,
      variant: sourceCfg.variant,
      tooltip: sourceCfg.tooltipBase,
    });
  }

  if (reEntryConfig?.enabled) {
    const meta = getReEntryFlavourMeta(reEntryConfig.type);
    const used = reEntryCount ?? 0;
    const cap = reEntryConfig.count;
    chips.push({
      label: `${meta.label} ${used}/${cap}`,
      variant: meta.variant,
      tooltip: `${meta.label} — ${used} of ${cap} ${pluralise('attempt', cap)} used`,
    });
  }

  if (reExecuteConfig?.enabled) {
    const used = tpReexecCount ?? 0;
    const cap = reExecuteConfig.count;
    chips.push({
      label: `${TP_REEXEC_PILL_META.label} ${used}/${cap}`,
      variant: TP_REEXEC_PILL_META.variant,
      tooltip: `${TP_REEXEC_PILL_META.label} — ${used} of ${cap} ${pluralise('attempt', cap)} used`,
    });
  }

  return chips;
}

